import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowRight,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaStore,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { supabase } from '../supabaseClient';

// LOT0F10_MERCHANT_INVITE_CLIENT_V1
const MERCHANT_INVITE_SESSION_KEY = "forfeo:merchant-onboarding-invite:v1";

type MerchantInviteContext = {
  token: string;
  business_name?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  region?: string | null;
  prp_id?: string | null;
  lead_id?: string | null;
  expires_at?: string | null;
};

type MerchantState = {
  request_id?: string | null;
  business_id?: string | null;
  business_name?: string | null;
  contact_email?: string | null;
  status?: 'not_started' | 'under_review' | 'approved' | 'rejected' | 'suspended' | string;
  is_approved?: boolean | null;
  can_manage?: boolean | null;
  review_reason?: string | null;
  submitted_at?: string | null;
  reviewed_at?: string | null;
};

const firstRow = <T,>(value: T | T[] | null | undefined): T | null => {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
};

const inviteTokenFromBrowser = (): string => {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = String(params.get("invite") || "").trim();

  if (/^[A-Za-z0-9_-]{40,120}$/.test(fromUrl)) {
    try {
      sessionStorage.setItem(MERCHANT_INVITE_SESSION_KEY, fromUrl);
      window.history.replaceState({}, document.title, "/register-business");
    } catch (_) {}
    return fromUrl;
  }

  try {
    const saved = String(sessionStorage.getItem(MERCHANT_INVITE_SESSION_KEY) || "").trim();
    return /^[A-Za-z0-9_-]{40,120}$/.test(saved) ? saved : "";
  } catch (_) {
    return "";
  }
};

const clearInviteToken = () => {
  try {
    sessionStorage.removeItem(MERCHANT_INVITE_SESSION_KEY);
  } catch (_) {}
};

const invokeMerchantInvite = async (
  action: "preview" | "consume",
  token: string,
  extra: Record<string, unknown> = {},
) => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  const accessToken = session?.access_token?.trim();
  if (!accessToken) throw new Error("merchant_session_required");

  const result = await supabase.functions.invoke("merchant-onboarding-invite", {
    body: { action, token, ...extra },
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (result.error) {
    const errorData = result.data as Record<string, unknown> | null;
    throw new Error(
      typeof errorData?.error === "string"
        ? errorData.error
        : result.error.message || "merchant_invite_failed",
    );
  }

  const payload = (result.data || {}) as Record<string, any>;
  if (payload.ok !== true) throw new Error(String(payload.error || "merchant_invite_failed"));
  return payload;
};

const MerchantOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<MerchantState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [category, setCategory] = useState('');
  const [region, setRegion] = useState('');
  const [inviteContext, setInviteContext] = useState<MerchantInviteContext | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const loadState = useCallback(async () => {
    setLoading(true);
    setError(null);
    setInviteError(null);

    const pendingInviteToken = inviteTokenFromBrowser();
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError) {
      setError(sessionError.message);
      setLoading(false);
      return;
    }

    if (!sessionData.session?.user) {
      navigate(
        pendingInviteToken
          ? '/login?type=business&mode=signup'
          : '/login?type=business&mode=login',
        { replace: true },
      );
      return;
    }

    const { data, error: stateError } = await supabase.rpc(
      'get_forfeo_merchant_onboarding_state',
    );

    if (stateError) {
      setError(stateError.message);
      setLoading(false);
      return;
    }

    const nextState = firstRow<MerchantState>(data);
    setState(nextState);

    if (nextState?.business_name) {
      setBusinessName(nextState.business_name);
    }

    if (pendingInviteToken) {
      try {
        const invitePayload = await invokeMerchantInvite("preview", pendingInviteToken);
        const invite = (invitePayload.invite || {}) as Record<string, any>;
        const context: MerchantInviteContext = {
          token: pendingInviteToken,
          business_name: invite.business_name || null,
          contact_name: invite.contact_name || null,
          contact_phone: invite.contact_phone || null,
          contact_email: invite.contact_email || null,
          region: invite.region || null,
          prp_id: invite.prp_id || null,
          lead_id: invite.lead_id || null,
          expires_at: invite.expires_at || null,
        };
        setInviteContext(context);
        if (!nextState?.business_name && context.business_name) setBusinessName(context.business_name);
        if (context.contact_name) setContactName(context.contact_name);
        if (context.contact_phone) setContactPhone(context.contact_phone);
        if (context.region) setRegion(context.region);
      } catch (inviteFailure: any) {
        setInviteError(inviteFailure?.message || "L’invitation d’onboarding n’a pas pu être validée.");
      }
    }

    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    void loadState();
  }, [loadState]);

  const submitRequest = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const prpId = inviteContext?.prp_id || localStorage.getItem('referred_by_prp');
      const closerId = localStorage.getItem('referred_by_closer');

      const { data: requestData, error: requestError } = await supabase.rpc(
        'request_forfeo_merchant_onboarding',
        {
          p_business_name: businessName.trim(),
          p_contact_name: contactName.trim() || null,
          p_contact_phone: contactPhone.trim() || null,
          p_website: website.trim() || null,
          p_category: category.trim() || null,
          p_region: region.trim() || null,
          p_prp_id: prpId || null,
          p_closer_id: closerId || null,
        },
      );

      if (requestError) throw requestError;

      if (prpId) localStorage.removeItem('referred_by_prp');
      if (closerId) localStorage.removeItem('referred_by_closer');

      if (inviteContext?.token) {
        try {
          const requestRow = firstRow<any>(requestData as any);
          await invokeMerchantInvite("consume", inviteContext.token, {
            request_id: requestRow?.request_id || null,
            human_confirmation: true,
          });
          clearInviteToken();
        } catch (consumeFailure: any) {
          setInviteError(
            `Votre demande commerçante est transmise, mais la clôture de l’invitation doit être vérifiée : ${consumeFailure?.message || "invite_consume_failed"}`,
          );
        }
      }

      setSuccess(
        "Votre demande commerçante a été transmise. L'administrateur doit l'approuver avant toute publication.",
      );

      await loadState();
    } catch (requestFailure: any) {
      setError(requestFailure?.message || "La demande commerçante n'a pas pu être transmise.");
    } finally {
      setSubmitting(false);
    }
  };

  const status = state?.status || 'not_started';
  const approved = state?.is_approved === true && state?.can_manage === true;
  const pending = status === 'under_review';
  const blocked = status === 'rejected' || status === 'suspended';

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="bg-slate-950 px-6 py-8 text-white sm:px-10">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10">
                <FaStore className="text-2xl" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-400">
                  Profil unifié Forfeo
                </p>
                <h1 className="mt-1 text-2xl font-black sm:text-3xl">
                  Demande de compte commerçant
                </h1>
              </div>
            </div>

            <p className="mt-5 max-w-3xl text-sm leading-6 text-slate-300 sm:text-base">
              Votre espace voyageur demeure actif. Le portail commerçant et la publication de votre établissement seront activés uniquement après une validation administrative.
            </p>
          </div>

          <div className="p-6 sm:p-10">
            {loading ? (
              <div className="flex items-center gap-3 rounded-2xl bg-slate-100 p-5 text-sm font-bold text-slate-600">
                <FaClock className="animate-pulse" /> Vérification de votre dossier…
              </div>
            ) : approved ? (
              <div data-merchant-onboarding-state="approved" className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <FaCheckCircle className="mt-1 shrink-0 text-3xl text-emerald-600" />
                  <div>
                    <h2 className="text-xl font-black text-emerald-950">Compte commerçant approuvé</h2>
                    <p className="mt-2 text-sm leading-6 text-emerald-800">
                      Votre portail commerçant est maintenant actif. Vous pouvez configurer votre établissement et préparer vos offres.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/business-dashboard')}
                      className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-700 px-5 py-3 font-black text-white transition hover:bg-emerald-800 sm:w-auto"
                    >
                      Ouvrir le tableau de bord <FaArrowRight />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {pending && (
                  <div data-merchant-onboarding-state="under_review" className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
                    <div className="flex items-start gap-3">
                      <FaClock className="mt-0.5 shrink-0 text-xl" />
                      <div>
                        <p className="font-black">Demande en cours d'examen</p>
                        <p className="mt-1 leading-6">Aucune offre ni réservation ne peut être publiée avant l'approbation de l'administrateur.</p>
                      </div>
                    </div>
                  </div>
                )}

                {blocked && (
                  <div data-merchant-onboarding-state={status} className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-900">
                    <div className="flex items-start gap-3">
                      <FaExclamationTriangle className="mt-0.5 shrink-0 text-xl" />
                      <div>
                        <p className="font-black">
                          {status === 'suspended' ? 'Compte commerçant suspendu' : 'Demande non approuvée'}
                        </p>
                        <p className="mt-1 leading-6">
                          {state?.review_reason || "Communiquez avec l'administration Forfeo ou mettez votre demande à jour."}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-700">
                    {success}
                  </div>
                )}

                {inviteContext && (
                  <div data-merchant-onboarding-invite="verified" className="mb-6 rounded-2xl border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900">
                    <p className="font-black">✓ Invitation Forfeo vérifiée</p>
                    <p className="mt-1 leading-6">
                      Votre dossier pour {inviteContext.business_name || "cette entreprise"} a été prérempli.
                      L’attribution au PRP est conservée, mais vous devez encore soumettre vous-même la demande commerçante.
                    </p>
                  </div>
                )}

                {inviteError && (
                  <div data-merchant-onboarding-invite="error" className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-900">
                    {inviteError}
                  </div>
                )}

                <form onSubmit={submitRequest} data-merchant-onboarding-form className="grid gap-5 sm:grid-cols-2">
                  <label className="sm:col-span-2">
                    <span className="mb-2 block text-sm font-black text-slate-700">Nom de l'entreprise *</span>
                    <input
                      required
                      minLength={2}
                      maxLength={180}
                      value={businessName}
                      onChange={(event) => setBusinessName(event.target.value)}
                      className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                      placeholder="Nom légal ou commercial"
                    />
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-black text-slate-700">Personne-ressource</span>
                    <input value={contactName} onChange={(event) => setContactName(event.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-black text-slate-700">Téléphone</span>
                    <input value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" />
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-black text-slate-700">Catégorie</span>
                    <input value={category} onChange={(event) => setCategory(event.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" placeholder="Restaurant, hôtel, spa…" />
                  </label>

                  <label>
                    <span className="mb-2 block text-sm font-black text-slate-700">Région</span>
                    <input value={region} onChange={(event) => setRegion(event.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" placeholder="Québec, Montréal…" />
                  </label>

                  <label className="sm:col-span-2">
                    <span className="mb-2 block text-sm font-black text-slate-700">Site Web</span>
                    <input type="url" value={website} onChange={(event) => setWebsite(event.target.value)} className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100" placeholder="https://" />
                  </label>

                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      disabled={submitting || businessName.trim().length < 2}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-6 py-4 font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                    >
                      {submitting ? 'Transmission…' : pending ? 'Mettre la demande à jour' : 'Soumettre la demande'}
                      {!submitting && <FaArrowRight />}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MerchantOnboarding;
